import React, { useCallback, useContext, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useBusinessNonMockEntityIds } from "@hooks/useBusinessEntityIds";
import {
    BILLABLE_EXPENSE_DRILL_DOWN_AVAILABLE_FILTERS,
    DefaultBillableExpenseDrillDownTransactionView,
} from "@components/transactions/views/DefaultBillableExpenseDrillDownTransactionView";
import { DirectoryContext } from "../Directory.context";
import { CounterpartyDirectoryViewRow } from "../counterpartyDirectoryViewTypes";
import { CounterpartyCountCell } from "./CounterpartyCountCell";

export const CounterpartyBillableExpensesCell: React.FC<
    CellContext<ViewTableFeatures, CounterpartyDirectoryViewRow, number>
> = React.memo(({ row, table }) => {
    const { filters } = useContext(DirectoryContext);
    const businessEntityIds = useBusinessNonMockEntityIds();
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();

    const counterpartyId = row.original.counterparty?.id;
    const count = row.original.billableExpenseCount ?? 0;
    const isDisabled = !counterpartyId || count === 0;

    // Mirrors the entity scoping used elsewhere in the directory (e.g.
    // DirectoryGlDrilldownDrawer): fall back to every business entity when
    // the page-level entity filter is unset.
    const entityIds = useMemo(
        () =>
            filters.entityIds?.length ? filters.entityIds : businessEntityIds,
        [filters.entityIds, businessEntityIds],
    );

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!counterpartyId) {
                return;
            }
            openGlobalTransactionViewDrawer(
                { customerIds: [counterpartyId], entityIds },
                {
                    baseViewOverride:
                        DefaultBillableExpenseDrillDownTransactionView,
                    availableFilters:
                        BILLABLE_EXPENSE_DRILL_DOWN_AVAILABLE_FILTERS,
                },
            );
        },
        [counterpartyId, entityIds, openGlobalTransactionViewDrawer],
    );

    return (
        <CounterpartyCountCell
            count={count}
            isDisabled={isDisabled}
            table={table}
            onClick={handleClick}
        />
    );
});

CounterpartyBillableExpensesCell.displayName =
    "CounterpartyBillableExpensesCell";
